import { useEffect } from "react";
import LegalFooter from "@/components/LegalFooter";

export default function DataCollection() {
  useEffect(() => {
    document.title = "Data Collection — Comms Fusion Pro";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description");
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute("content", "Data collection disclosures for Comms Fusion Pro — exactly what we collect, why, and how it's used.");
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = window.location.origin + "/data-collection";
  }, []);

  const updated = "May 5, 2026";

  const rows: { category: string; data: string; purpose: string; basis: string; retention: string }[] = [
    { category: "Account", data: "Name, email, hashed password, workspace ID, role", purpose: "Authentication, access control", basis: "Contract", retention: "Lifetime of account + 30 days" },
    { category: "WhatsApp Business", data: "WABA ID, phone number ID, display number, verified name, access token", purpose: "Send/receive WhatsApp messages, manage templates", basis: "Contract", retention: "Until disconnect" },
    { category: "Contacts", data: "Phone, name, tags, custom fields you upload", purpose: "CRM, segmentation, messaging", basis: "Controller (you) consent", retention: "Until you delete" },
    { category: "Conversations", data: "Inbound/outbound messages, media, timestamps, status, wamid", purpose: "Inbox, automation, analytics", basis: "Contract", retention: "Until you delete or workspace closed" },
    { category: "Automations & Templates", data: "Flow definitions, message templates, triggers", purpose: "Execute customer-defined automations", basis: "Contract", retention: "Until you delete" },
    { category: "Usage logs", data: "IP, user agent, action, timestamp, error traces", purpose: "Security, abuse prevention, debugging", basis: "Legitimate interest", retention: "90 days" },
    { category: "Billing", data: "Plan, invoices, payment method ref (no card numbers stored)", purpose: "Subscription management", basis: "Contract / legal", retention: "7 years (tax)" },
    { category: "AI features", data: "Message text sent to AI providers when AI features are used", purpose: "AI replies, summaries, classification", basis: "Contract", retention: "Not retained by us beyond logs" },
  ];

  return (
    <>
      <main className="min-h-screen bg-background text-foreground py-12 px-6">
        <article className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold mb-2">Data Collection Disclosures</h1>
          <p className="text-sm text-muted-foreground mb-8">Last updated: {updated}</p>

          <section className="space-y-6 text-sm leading-relaxed">
            <p>
              This page is a transparent breakdown of every category of data Comms Fusion
              Pro collects, why we collect it, the legal basis, and how long we keep it.
              For the full policy, see our{" "}
              <a href="/privacy" className="text-primary underline">Privacy Policy</a>.
            </p>

            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-semibold">Category</th>
                    <th className="px-3 py-2 font-semibold">Data Collected</th>
                    <th className="px-3 py-2 font-semibold">Purpose</th>
                    <th className="px-3 py-2 font-semibold">Legal Basis</th>
                    <th className="px-3 py-2 font-semibold">Retention</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t border-border align-top">
                      <td className="px-3 py-2 font-medium">{r.category}</td>
                      <td className="px-3 py-2">{r.data}</td>
                      <td className="px-3 py-2">{r.purpose}</td>
                      <td className="px-3 py-2">{r.basis}</td>
                      <td className="px-3 py-2">{r.retention}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2 className="text-xl font-semibold mt-8">Sub-processors</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Meta / WhatsApp Business Platform</strong> — message delivery</li>
              <li><strong>Supabase</strong> — database, authentication, storage, edge functions</li>
              <li><strong>Lovable AI Gateway</strong> — AI features (Google Gemini, OpenAI models)</li>
              <li><strong>ElevenLabs</strong> — voice/TTS (only when enabled)</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8">What we do NOT collect</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>We do not sell personal data.</li>
              <li>We do not use your message content for advertising.</li>
              <li>We do not train third-party AI models on your data.</li>
              <li>We do not store payment card numbers (handled by payment processor).</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8">Cookies & Local Storage</h2>
            <p>
              We use essential cookies/local storage for authentication sessions and UI
              preferences. We do not use third-party advertising or tracking cookies.
            </p>

            <h2 className="text-xl font-semibold mt-8">Your Rights</h2>
            <p>
              You can access, export, or delete your data at any time. Contact{" "}
              <a href="mailto:support@klicko.org" className="text-primary underline">
                support@klicko.org
              </a>.
            </p>
          </section>
        </article>
      </main>
      <LegalFooter />
    </>
  );
}
